import { Alert, Box, CircularProgress, Tab, Tabs } from '@mui/material';
import { type ReactNode, useState } from 'react';
import { useSearchParams } from 'react-router';

import { RelatedCountFields } from '~/components/common/RelatedCountFields';
import { tabLabelWithCount } from '~/components/common/TabCount';
import { useKnowledgeEnabled } from '~/components/knowledge/useKnowledgeEnabled';
import { AiSummaryCard, type PublishMode } from '~/components/tasks/detail/AiSummaryCard';
import { LogUpdateDialog } from '~/components/tasks/detail/LogUpdateDialog';
import { PublishKnowledgeDialog } from '~/components/tasks/detail/PublishKnowledgeDialog';
import { TaskAttachmentsTab } from '~/components/tasks/detail/TaskAttachmentsTab';
import { TaskContractsTab, useTaskContracts } from '~/components/tasks/detail/TaskContractsTab';
import { TaskElevatorsTab, useTaskCars } from '~/components/tasks/detail/TaskElevatorsTab';
import { TaskHeader } from '~/components/tasks/detail/TaskHeader';
import { taskKnowledgeEntries, TaskKnowledgeTab } from '~/components/tasks/detail/TaskKnowledgeTab';
import { systemLogIds, UpdateLog } from '~/components/tasks/detail/UpdateLog';
import { NewTaskDialog } from '~/components/tasks/NewTaskDialog';
import { asksForKnowledge } from '~/components/tasks/taskStyle';
import type { TaskDetail } from '~/data/generated/model';
import { usePropertyKnowledge } from '~/data/hooks/useKnowledge';
import { useTask, useTaskActions } from '~/data/hooks/useTaskDetail';
import { useTaskStatuses } from '~/data/hooks/useTaskStatuses';
import { DetailTableHeightProvider } from '~/hooks/useDetailTableHeight';
import { pageBandColor, SURFACE_RADIUS } from '~/theme';

const TASK_TABS = ['activity', 'elevators', 'attachments', 'knowledge', 'contract'] as const;
type TaskTab = (typeof TASK_TABS)[number];

const TAB_LABEL: Record<TaskTab, ReactNode> = {
  activity: 'Activity',
  elevators: (
    <>
      Elevators
      <Box component="span" sx={{ fontSize: '0.75em', fontWeight: 400, opacity: 0.75 }}>
        (+ records)
      </Box>
    </>
  ),
  attachments: 'Attachments',
  knowledge: 'Knowledge',
  contract: 'Governing contract',
};

const COUNT_LABEL: Record<TaskTab, string> = {
  activity: 'Activity',
  elevators: 'Elevators',
  attachments: 'Attachments',
  knowledge: 'Knowledge',
  contract: 'Governing contract',
};

const isTaskTab = (value: string | null): value is TaskTab =>
  (TASK_TABS as readonly (string | null)[]).includes(value);

type Props = {
  taskId: string;
  variant: 'page' | 'dialog' | 'preview';
  /**
   * Where a completion that asks what the team should remember goes. The preview
   * passes its list's queue, because completing a task can filter it out of that list,
   * which closes the preview and would take a prompt of its own with it. Without it
   * the view opens its own dialog.
   */
  onCompleted?: (task: TaskDetail) => void;
};

export function TaskDetailView({ taskId, variant, onCompleted }: Props) {
  const { task, error, isLoading } = useTask(taskId);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error || !task) {
    return <Box sx={{ p: 6, textAlign: 'center', color: '#777' }}>Task not found.</Box>;
  }
  return <LoadedTask task={task} variant={variant} onCompleted={onCompleted} />;
}

function useTabState(variant: Props['variant']): [TaskTab, (tab: TaskTab) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const [local, setLocal] = useState<TaskTab>('activity');
  if (variant !== 'page') return [local, setLocal];
  const param = searchParams.get('tab');
  const selectTab = (next: TaskTab) =>
    setSearchParams(
      (prev) => {
        if (next === 'activity') prev.delete('tab');
        else prev.set('tab', next);
        return prev;
      },
      { replace: true },
    );
  return [isTaskTab(param) ? param : 'activity', selectTab];
}

function LoadedTask({
  task,
  variant,
  onCompleted,
}: {
  task: TaskDetail;
  variant: Props['variant'];
  onCompleted: Props['onCompleted'];
}) {
  const { updateTask, addLog, addEvidence, setStatus } = useTaskActions(task.id);
  const { statuses } = useTaskStatuses();
  const knowledgeEnabled = useKnowledgeEnabled();
  const knowledge = usePropertyKnowledge(knowledgeEnabled ? task.propertyId : null);
  const cars = useTaskCars(task);
  const contracts = useTaskContracts(task);
  const [tab, selectTab] = useTabState(variant);
  const [logOpen, setLogOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [writeError, setWriteError] = useState<string | null>(null);
  const [publishMode, setPublishMode] = useState<PublishMode | null>(null);

  const relevantKnowledge = taskKnowledgeEntries(knowledge.entries, task).filter(
    (entry) => !entry.supersededById,
  );
  const tabs = TASK_TABS.filter((key) => key !== 'knowledge' || knowledgeEnabled);
  const current = tabs.includes(tab) ? tab : 'activity';

  const attempt = async <T,>(write: () => Promise<T>, message: string) => {
    setWriteError(null);
    try {
      return await write();
    } catch (err) {
      setWriteError(message);
      throw err;
    }
  };

  const change = async (write: () => Promise<TaskDetail>, message: string) => {
    setBusy(true);
    try {
      return await attempt(write, message);
    } catch {
      return null;
    } finally {
      setBusy(false);
    }
  };

  const changeStatus = async (statusId: string) => {
    const from = task.status;
    const updated = await change(() => setStatus(statusId), 'Could not change the task status.');
    if (updated && asksForKnowledge(updated, from)) {
      if (onCompleted) onCompleted(updated);
      else setPublishMode('complete');
    }
  };

  const counts: Record<TaskTab, number | undefined> = {
    activity: task.logs.length - systemLogIds(task).size,
    elevators:
      task.cars.length > 0 ? task.cars.length : cars.loading ? undefined : cars.rows.length,
    attachments: task.evidence.length,
    knowledge: knowledge.isLoading ? undefined : relevantKnowledge.length,
    contract: contracts.isLoading ? undefined : contracts.covering.length,
  };

  const panels: Record<TaskTab, () => ReactNode> = {
    activity: () => <UpdateLog task={task} onLogUpdate={() => setLogOpen(true)} />,
    elevators: () => <TaskElevatorsTab task={task} />,
    attachments: () => (
      <TaskAttachmentsTab
        task={task}
        onUpload={(file) => addEvidence(file, undefined)}
        onShowUpdate={() => selectTab('activity')}
      />
    ),
    knowledge: () => <TaskKnowledgeTab task={task} />,
    contract: () => <TaskContractsTab task={task} />,
  };

  const isPreview = variant === 'preview';

  return (
    <DetailTableHeightProvider>
      <Box
        component={variant === 'page' ? 'main' : 'div'}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100%',
          bgcolor: isPreview ? pageBandColor : 'grey.50',
        }}
      >
        <TaskHeader
          task={task}
          statuses={statuses}
          busy={busy}
          backTo={variant === 'page' ? '/tasks' : null}
          compact={isPreview}
          footer={
            isPreview ? (
              // The preview has no tab strip, so the counts its tabs would carry are listed instead.
              <RelatedCountFields
                counts={tabs.map((key) => ({ label: COUNT_LABEL[key], value: counts[key] }))}
              />
            ) : null
          }
          aiSummary={
            <AiSummaryCard
              task={task}
              onPublish={setPublishMode}
              onViewKnowledge={isPreview ? undefined : () => selectTab('knowledge')}
              autoGenerate={!isPreview}
            />
          }
          onStatusChange={(statusId) => void changeStatus(statusId)}
          onFollowUpChange={(followUpDate) =>
            void change(() => updateTask({ followUpDate }), 'Could not change the follow-up date.')
          }
          onEdit={() => setEditOpen(true)}
          onLogUpdate={() => {
            selectTab('activity');
            setLogOpen(true);
          }}
        />
        {isPreview ? null : (
          <>
            <Tabs
              value={current}
              onChange={(_event, next: TaskTab) => selectTab(next)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              aria-label="Task sections"
              sx={{
                backgroundColor: pageBandColor,
                px: { xs: 2, md: 3 },
                borderBottomLeftRadius: SURFACE_RADIUS,
                borderBottomRightRadius: SURFACE_RADIUS,
              }}
            >
              {tabs.map((key) => (
                <Tab key={key} value={key} label={tabLabelWithCount(TAB_LABEL[key], counts[key])} />
              ))}
            </Tabs>
          </>
        )}
        {writeError ? (
          <Alert severity="error" onClose={() => setWriteError(null)} sx={{ m: 2, mb: 0 }}>
            {writeError}
          </Alert>
        ) : null}
        {isPreview ? null : <Box sx={{ flexGrow: 1, minWidth: 0, p: 2 }}>{panels[current]()}</Box>}
      </Box>
      {logOpen ? (
        <LogUpdateDialog
          onClose={() => setLogOpen(false)}
          onSave={(labelId, text) =>
            attempt(() => addLog({ labelId, text }), 'Could not save the log.')
          }
          onAttach={addEvidence}
        />
      ) : null}
      <NewTaskDialog
        open={editOpen}
        task={task}
        onClose={() => setEditOpen(false)}
        onSave={updateTask}
      />
      {knowledgeEnabled && publishMode ? (
        <PublishKnowledgeDialog
          task={task}
          mode={publishMode}
          onClose={() => setPublishMode(null)}
        />
      ) : null}
    </DetailTableHeightProvider>
  );
}
